import { useIntl } from 'react-intl';
import { useToast } from '../../../../../../modules/components/toast/ToastContext';
import { toAbsoluteUrl, valueHandler } from '../../../../../../../_metronic/helpers';
import clsx from 'clsx';
import RowHeaderTable from '../../table/RowHeaderTable';
import TimelineWithoutDate from '../../../../../../modules/components/timeline/TimelineWithoutDate';
import HttpCodeBadge, {
  parseHttpStatus,
} from '../../../../../../modules/components/badge/HttpCodeBadge';
import { normalizeVpn, Vpn, vpnLabel } from '../../../../mappings';

interface Props {
  data: any;
  screenshotData: any;
  status: string;
  refetch: () => void;
  onExpandImage: (area: string) => void;
}

export default function Detail({ data, screenshotData, status, refetch, onExpandImage }: Props) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id: `SI_DOMAIN.${id}` });
  const { setToast } = useToast();
  const emDash = '—';

  const geoData = data?.result?.geo_info?.results;
  const geoDataKeys = Object.keys(geoData ?? {});

  const getScreenshotPath = (area: string) => {
    const filename = screenshotData?.find((item) => item?.vpn === area)?.file_path;
    return filename
      ? `${process.env.REACT_APP_SITE_INTELLIGENCE_IMG_BASE_URL}captures${filename}`
      : '';
  };

  return (
    <div className='card mb-5 mb-xl-10 border'>
      <div className='card-header align-items-center justify-content-start'>
        <h4 className='my-6'>{t('GEO_TARGETING')}</h4>
      </div>
      <div className='card-body p-9 d-flex flex-column gap-9'>
        {!geoData && (
          <div className='text-center'>{intl.formatMessage({ id: 'COMMON.NO_RECORD_FOUND' })} </div>
        )}

        {geoData &&
          Object.keys(vpnLabel).map((area) => {
            const geoDataAreaKey = geoDataKeys.find((key) => normalizeVpn(key) === area);
            const screenshotPath = getScreenshotPath(geoDataAreaKey);
            return (
              <div key={area}>
                <h5 className='fw-bolder'>
                  {intl.formatMessage({ id: vpnLabel[area as Vpn] })}
                </h5>

                {geoData?.[geoDataAreaKey] && screenshotPath ? (
                  <div className='row g-5 g-md-9'>
                    {/* Left column: Screenshot */}
                    <div className='col-12 col-xl-6 d-flex flex-column gap-6'>
                      <div
                        className='position-relative shadow-sm rounded overflow-hidden bg-gray-100'
                        style={{ height: 'clamp(220px, 32vw, 350px)' }}
                      >
                        {screenshotPath ? (
                          <>
                            <button
                              type='button'
                              className='btn btn-dark bg-dark bg-opacity-75 position-absolute end-0 mt-2 me-2 py-1 px-2'
                              onClick={() => onExpandImage(screenshotPath)}
                              title={t('VIEW_IMAGE')}
                            >
                              <i className='bi bi-arrows-angle-expand p-0 fs-6'></i>
                            </button>
                            <img
                              src={screenshotPath}
                              className='w-100 h-100 rounded'
                              style={{ objectFit: 'contain' }}
                            />
                          </>
                        ) : (
                          // No image placeholder
                          <div className='w-100 h-100 rounded bg-gray-200 d-flex align-items-center justify-content-center ratio ratio-16x9'>
                            <img src={toAbsoluteUrl('/media/icons/duotune/general/gen006.svg')} />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right column: Details */}
                    <div className='col-12 col-xl-6'>
                      <div className='vstack gap-2'>
                        <RowHeaderTable
                          rows={[
                            {
                              key: 'status',
                              header: t('STATUS'),
                              value: valueHandler(geoData?.[geoDataAreaKey]?.status),
                            },
                            {
                              key: 'title',
                              header: t('TITLE'),
                              value: valueHandler(geoData?.[geoDataAreaKey]?.title),
                            },
                            {
                              key: 'description',
                              header: t('DESCRIPTION'),
                              value: valueHandler(geoData?.[geoDataAreaKey]?.description),
                            },
                            {
                              key: 'keywords',
                              header: t('KEYWORDS'),
                              value: valueHandler(geoData?.[geoDataAreaKey]?.keywords),
                            },
                          ]}
                        />
                        {geoData?.[geoDataAreaKey]?.redirect_history?.length > 0 && (
                          <div className='vstack gap-2'>
                            <span className='fw-bolder'>{t('REDIRECT')}</span>
                            <div>
                              {geoData?.[geoDataAreaKey]?.redirect_history?.map(
                                (item, idx, arr) => {
                                  const { label, color } = parseHttpStatus(item.status);
                                  const localizedHttpLabelMap: Record<string, string> = {
                                    Success: t('HTTP_STATUS_SUCCESS'),
                                    Redirect: t('HTTP_STATUS_REDIRECT'),
                                    Error: t('HTTP_STATUS_ERROR'),
                                  };
                                  return (
                                    <TimelineWithoutDate key={idx} badgeColor={`text-${color}`}>
                                      <div
                                        className={clsx('vstack', idx !== arr.length - 1 && 'mb-4')}
                                      >
                                        <a
                                          href={item.url}
                                          target='_blank'
                                          rel='noopener noreferrer'
                                          className='text-break'
                                        >
                                          {item.url}
                                        </a>
                                        <span>
                                          <span className='font-monospace fs-7'>
                                            {item?.ip &&
                                              `${item?.ip}${item?.port && `:${item?.port}`} `}
                                          </span>
                                          <HttpCodeBadge
                                            code={item.status}
                                            variant='custom'
                                            color={color}
                                            customLabel={localizedHttpLabelMap[label] ?? label}
                                          />
                                        </span>
                                      </div>
                                    </TimelineWithoutDate>
                                  );
                                }
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>{t('NOT_AVAILABLE')}</div>
                )}
              </div>
            );
          })}
      </div>
      <div className='d-flex justify-content-end mb-5'>
        <a href='#summary' className='px-8 d-none print-display-button'>
          <i className="bi bi-caret-up-square-fill fs-3"></i>
        </a>
      </div>
    </div>
  );
}
